import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createBin,
  deleteBinById,
  getBins,
  updateBinById,
  updateBinStatusById,
} from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { delBtn, plusBtn } from "../../../util/CommonIconStyle";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import { canWriteModule } from "../../../util/roleModuleAccess";

const initValues = {
  name: "",
  locationId: "",
};
const initErrs = {
  name: false,
  locationId: false,
};
const initHlps = {
  name: "",
  locationId: "",
};

const tableHeaders = [
  "name",
  "locationName",
  "locationType",
  "activeStatus",
  "extraStatus",
];

const toBinList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.bins)) return payload.bins;
  return [];
};

const getBinId = (bin) => bin?.id ?? bin?.binId ?? null;

const isDefaultBin = (bin) =>
  bin?.default === true || bin?.default === "true" || bin?.isDefault === true;

const isBinActive = (bin) => {
  const status = bin?.status;
  if (status === true || status === "true" || status === "Y" || status === 1) {
    return true;
  }
  if (
    status === false ||
    status === "false" ||
    status === "N" ||
    status === 0
  ) {
    return false;
  }
  return isActiveRecord(bin);
};

const getLocationType = (loc) => {
  const type = loc?.locationType;
  if (typeof type === "string") return type.trim();
  if (typeof type === "number" && Number.isFinite(type)) return String(type);
  return "";
};

const hasLocationType = (loc) => getLocationType(loc).length > 0;

const isConsumptionLocation = (loc) =>
  getLocationType(loc).toLowerCase() === "consumption";

const toDisplayText = (value, fallback = "-") => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
};

const asLocationList = (locations) =>
  Array.isArray(locations)
    ? locations.filter((loc) => loc && typeof loc === "object")
    : [];

const readSessionLocations = () => {
  try {
    const raw = sessionStorage.getItem("mandateData");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.locations) ? parsed.locations : [];
  } catch {
    return [];
  }
};

const resolveBinLocation = (bin, locations) => {
  const nested =
    bin?.location &&
    typeof bin.location === "object" &&
    !Array.isArray(bin.location)
      ? bin.location
      : null;
  const locationId =
    bin?.locationId ?? nested?.id ?? nested?.locationId ?? null;
  const match = asLocationList(locations).find(
    (loc) => locationId != null && String(loc?.id) === String(locationId),
  );
  return {
    locationId:
      locationId != null && locationId !== "" ? String(locationId) : "",
    locationName: toDisplayText(
      bin?.locationName ??
        nested?.locationName ??
        nested?.name ??
        match?.locationName,
    ),
    locationType: toDisplayText(
      typeof bin?.locationType === "string" ||
        typeof bin?.locationType === "number"
        ? bin.locationType
        : (nested?.locationType ?? match?.locationType),
    ),
  };
};

const Bin = () => {
  const dispatch = useDispatch();
  const canManageInventory = canWriteModule("inventory");
  const { binList } = useSelector((state) => state?.clinic);
  const [searchValue, setSearchValue] = useState("");
  const [modVsble, setModVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [statusModVsble, setStatusModVsble] = useState(false);
  const [binValues, setBinValues] = useState(initValues);
  const [binErrors, setBinErrors] = useState(initErrs);
  const [binHelps, setBinHelps] = useState(initHlps);
  const [selectedBin, setSelectedBin] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [sessionLocations, setSessionLocations] =
    useState(readSessionLocations);

  useEffect(() => {
    const syncLocations = () => setSessionLocations(readSessionLocations());
    window.addEventListener("mandate-session-updated", syncLocations);
    return () =>
      window.removeEventListener("mandate-session-updated", syncLocations);
  }, []);

  useEffect(() => {
    onBinApiCall();
  }, [dispatch]);

  const locationOptions = useMemo(() => {
    return asLocationList(sessionLocations)
      .filter(
        (loc) =>
          loc?.id != null &&
          String(loc.id).trim() !== "" &&
          isActiveRecord(loc) &&
          hasLocationType(loc) &&
          !isConsumptionLocation(loc),
      )
      .map((loc) => ({
        name: String(loc?.locationName ?? "").trim(),
        value: String(loc.id),
        locationType: getLocationType(loc),
      }))
      .filter(
        (loc) =>
          loc?.value &&
          loc.value !== "undefined" &&
          loc.value !== "null" &&
          loc?.name,
      );
  }, [sessionLocations]);

  const selectedLocationOption =
    locationOptions.find(
      (opt) => opt?.value === String(binValues?.locationId ?? ""),
    ) ?? null;

  const onBinApiCall = () => {
    try {
      dispatch(getBins())
        .then((res) => {
          const payload = res?.payload;
          if (payload == null) return;
          try {
            patchMandateSession({ bins: toBinList(payload) });
          } catch {
            // ignore mandate patch errors
          }
        })
        .catch(() => {});
    } catch {
      // ignore bin refresh errors
    }
  };

  const handleCreate = () => {
    setModVsble(true);
  };

  const handleModClose = () => {
    setModVsble(false);
    setBinValues(initValues);
    setBinErrors(initErrs);
    setBinHelps(initHlps);
    setSelectedBin(null);
  };

  const handleChange = (name, value) => {
    const empty =
      name === "locationId"
        ? value == null || value === ""
        : !String(value ?? "").trim();
    setBinValues({ ...binValues, [name]: value });
    setBinErrors({ ...binErrors, [name]: empty });
    setBinHelps({
      ...binHelps,
      [name]: empty ? "This is required field" : "",
    });
  };

  const handleEdit = (bin) => {
    if (isDefaultBin(bin)) return;
    const resolved = resolveBinLocation(bin, sessionLocations);
    setBinValues({
      name: toDisplayText(bin?.name, ""),
      locationId:
        resolved.locationId != null && resolved.locationId !== ""
          ? String(resolved.locationId)
          : "",
    });
    setBinErrors(initErrs);
    setBinHelps(initHlps);
    setSelectedBin(bin);
    setModVsble(true);
  };

  const handleDelete = (bin) => {
    if (isDefaultBin(bin)) return;
    setSelectedBin(bin);
    setDelModVsble(true);
  };

  const handleChangeSwitch = (bin) => {
    if (!getBinId(bin)) return;
    setSelectedBin(bin);
    setPendingStatus(!isBinActive(bin));
    setStatusModVsble(true);
  };

  const handleCloseStatusMod = () => {
    setStatusModVsble(false);
    setPendingStatus(null);
    setSelectedBin(null);
  };

  const handleConfirmStatusUpdate = () => {
    const binId = getBinId(selectedBin);
    if (!binId || pendingStatus == null) return;
    dispatch(
      updateBinStatusById({
        binId,
        status: pendingStatus === true,
      }),
    )
      .then((res) => {
        if (res?.payload) {
          onBinApiCall();
          handleCloseStatusMod();
        }
      })
      .catch(() => {});
  };

  const getTableBins = () => {
    try {
      const list = Array.isArray(binList) ? binList : [];
      const query = String(searchValue ?? "").toLowerCase();
      return list
        .filter((bin) => bin && typeof bin === "object")
        .filter((bin) =>
          toDisplayText(bin?.name, "").toLowerCase().includes(query),
        )
        .map((bin) => {
          const resolved = resolveBinLocation(bin, sessionLocations);
          const locked = isDefaultBin(bin);
          return {
            ...bin,
            name: toDisplayText(bin?.name, ""),
            locationName: resolved.locationName,
            locationType: resolved.locationType,
            activeStatus: (
              <div className="flex-row-ali-cen">
                {canManageInventory ? (
                  <CustomSwitch
                    value={isBinActive(bin)}
                    onChange={() => handleChangeSwitch(bin)}
                    greenToGray
                  />
                ) : null}
                <div
                  style={{ width: 80 }}
                  className={canManageInventory ? "ml10" : ""}
                >
                  {isBinActive(bin) ? (
                    <Typography className="green4 grn-bck status-card fs12 font-medium">
                      Active
                    </Typography>
                  ) : (
                    <Typography className="red11 red-back status-card fs12 font-medium">
                      Inactive
                    </Typography>
                  )}
                </div>
              </div>
            ),
            extraStatus:
              canManageInventory && !locked ? (
                <div className="flex-row">
                  <div className="cursor" onClick={() => handleDelete(bin)}>
                    <div className="flex-center">
                      <DeleteOutlineIcon sx={delBtn} />
                    </div>
                    <Typography className="red3 fs8 txt-semi-bold text-align-center">
                      Delete
                    </Typography>
                  </div>
                  <div className="cursor ml10" onClick={() => handleEdit(bin)}>
                    <div className="flex-center">
                      <EditIcon sx={plusBtn} />
                    </div>
                    <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                      Edit
                    </Typography>
                  </div>
                </div>
              ) : null,
          };
        });
    } catch {
      return [];
    }
  };

  const handleValidate = () => {
    const nameEmpty = !String(binValues?.name ?? "").trim();
    const locationEmpty = !locationOptions.some(
      (opt) => opt?.value === String(binValues?.locationId ?? ""),
    );
    const errObj = {
      name: nameEmpty,
      locationId: locationEmpty,
    };
    const hlpObj = {
      name: nameEmpty ? "This is required field" : "",
      locationId: locationEmpty ? "This is required field" : "",
    };
    setBinErrors({ ...binErrors, ...errObj });
    setBinHelps({ ...binHelps, ...hlpObj });
    return nameEmpty || locationEmpty;
  };

  const handleSave = () => {
    if (handleValidate()) return;
    const payload = {
      name: String(binValues?.name ?? "").trim(),
      locationId: binValues?.locationId,
    };
    const binId = getBinId(selectedBin);
    if (binId) {
      dispatch(updateBinById({ binId, ...payload }))
        .then((res) => {
          if (res?.payload) {
            onBinApiCall();
            handleModClose();
          }
        })
        .catch(() => {});
      return;
    }
    dispatch(createBin(payload))
      .then((res) => {
        if (res?.payload) {
          onBinApiCall();
          handleModClose();
        }
      })
      .catch(() => {});
  };

  const handleCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedBin(null);
  };

  const handleDeleteBin = () => {
    const binId = getBinId(selectedBin);
    if (!binId) return;
    dispatch(deleteBinById(binId))
      .then((res) => {
        if (res?.payload) {
          onBinApiCall();
          handleCloseDelMod();
        }
      })
      .catch(() => {});
  };

  return (
    <Container maxWidth="xl">
      <div className="back-white pv10">
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="flex-row-center"
        >
          <div className="flex-row">
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ width: "400px" }}
            >
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightBlue2}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </div>
          {canManageInventory ? (
            <div className="w5Per ml20">
              <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                <div>
                  <CustomButton text="Create" smallBtn onClick={handleCreate} />
                </div>
              </Grid>
            </div>
          ) : null}
        </Grid>

        <CustomTable
          columns={tableHeaders}
          datas={getTableBins()}
          grey
          maxHeight="68vh"
        />
      </div>

      <CustomModal
        open={modVsble}
        onClose={handleModClose}
        header={selectedBin ? "Edit" : "Create"}
        modalWidth={30}
        rightModal
      >
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                name="name"
                fullWidth
                value={binValues?.name}
                error={binErrors?.name}
                helperText={binHelps?.name}
                handleChange={(e) => handleChange("name", e?.target?.value)}
                label="Name"
                required={true}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="mt5">
              <CustomSelect
                list={locationOptions}
                value={selectedLocationOption}
                error={binErrors?.locationId}
                label="Location *"
                helperText={binHelps?.locationId}
                handleChange={(e, option) =>
                  handleChange("locationId", option?.value ?? "")
                }
                search
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt10">
              <div>
                <CustomButton
                  text={selectedBin ? "Update" : "Submit"}
                  smallBtn
                  onClick={handleSave}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDelMod}
        header="Delete Bin"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete this bin?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={handleDeleteBin}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={statusModVsble}
        onClose={handleCloseStatusMod}
        header="Update Status"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to update the status?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="flex1-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                onClick={handleConfirmStatusUpdate}
                smallBtn
                redBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseStatusMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </Container>
  );
};

export default Bin;
